import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { performSloCheck } from "@thkStore/Common/Utils/SloService";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    options?: InjectableCss;
    buttons?: InjectableCss;
    brand?: InjectableCss;
    mainSearch?: InjectableCss;
    dropDownSearch?: InjectableCss;
    mainMenu?: InjectableCss;
    menuContainer?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            padding: 15px 30px 0 !important;

            @media (max-width: 576px) {
                padding: 10px 5% 0 !important;
            }
        `,
    },
    skipToContent: {
        skipToContent: {
            css: css`
                display: none;
            `,
        },
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            position: relative;

            & > div {
                flex: auto;

                &:last-child{                    

                    @media (max-width:991px){
                        width: 100%;
                        justify-content: flex-end;

                        & > [class^="Wrapper-sc-"]{
                            width: auto;
                        }
                    }                
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    options: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;

            .search-button{

                @media (max-width: 767px), (min-width: 1366px) {
                    display: none;
                }

                &:hover{

                    span{
                        color: ${({ theme }) => theme.colors.primary.main};                    
                    }
                }

                /* Active state when the search is visible */
                &.active {
                    span {
                        color: ${({ theme }) => theme.colors.primary.main};
                    }
                }
            }

            button {
            
                &.button{
                    margin-top: 8px;
                }
            }
        `
    },
    buttons: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;

            @media (max-width: 991px) {
                display: none;
            }

            [data-test-selector="header_accountMenu"] {
                max-width: 110px;
            }

            button{

                &[class^="ButtonWrapper-sc-"]{
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    white-space: nowrap;
                }
            }
        `
    },
    brand: {
        css: css`
            height: 30px;

            img{
                width: 98px;
                height: 30px;
            }
        `
    },
    mainSearch: {
        css: css`
            width: 100%;
            padding: 0 16px;

            @media (max-width: 1365.98px) {
                display: none;
            }
        `
    },
    dropDownSearch: {
        css: css`
            background-color: ${({ theme }) => theme.colors.primary.contrast};
            position: absolute;
            width: calc(100% - 60px);
            padding: 40px 30px;
            border-radius: 0 0 5px 5px;
            box-shadow: 0px 4px 4px -1px rgba(0,0,0,0.12), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 4px 10px 0px rgba(0,0,0,0.12);
            z-index: 2;
            display: none;

            @media (max-width:992px){
                max-width: 930px;
            }

            @media (max-width:768px){
                max-width: 691px;
            }

            .close-link{
                position: absolute;
                right: 30px;
                top: 30px;
                display: flex;
                align-items: center;
                gap: 4px;
                z-index: 1;

                &:hover{

                    span{
                        color: ${({ theme }) => theme.colors.primary.main};
                    }
                }

                span{
                    color: #898989;

                    &[class^="IconWrapper-sc-"]{
                        width: 19px;
                        height: 19px;
                    }
                }
            }

            [class^="OverflowWrapper-sc-"] {
                width: 520px;
                display: flex;
                margin: 0 auto;
            }
        `
    },
    mainMenu: {
        css: css`
            width: 100%;

            @media (max-width: 991px) {
                order: 1;
            }
        `
    },
    menuContainer: {
        css: css`
            width: 100%;

            @media (max-width:991px){
                order: 1;
                width: auto;

                .mobile-wrapper{
                    justify-content: flex-end;

                    .mobile-menu-wrapper{

                        @media (max-width:577px){
                            order: 1;
                            margin-left: 24px;
                        }
                    }
                }
            }

            @media (min-width: 1366px) {
                width: auto;
            }
        `
    }
};

const styles = headerStyles;

const Header = ({ id, siteName, session, currentUserIsGuest, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const sloCheckRan = useRef(false);

    useEffect(() => {
        if (sloCheckRan.current) {
            return;
        }
        sloCheckRan.current = true;

        const isAuthenticated = !!session.userName && !currentUserIsGuest;
        performSloCheck(isAuthenticated);
    }, []);

    // Status to control the visibility of the search box
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    // Track current pathname to detect navigation changes
    const [currentPath, setCurrentPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '');

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    // Handler to show/hide the search box
    const toggleSearchHandler = () => {
        setIsSearchVisible(prevState => !prevState);
    };

    // Handler to close the search box
    const closeSearchHandler = () => {
        setIsSearchVisible(false);
    };

    // Effect to hide the search when navigation occurs
    useEffect(() => {
        const checkForNavigation = () => {
            const newPath = window.location.pathname;
            if (newPath !== currentPath) {
                setCurrentPath(newPath);
                setIsSearchVisible(false);
            }
        };

        // Check for navigation changes every 100ms
        const interval = setInterval(checkForNavigation, 100);

        // Cleanup interval on unmount
        return () => {
            clearInterval(interval);
        };
    }, [currentPath]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                cssOverrides: {
                    toast: css`
                        border-color: ${getColor("primary.main")};
                    `,
                },
                iconProps: {
                    color: "primary.main" as any,
                },
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <Page as="header" isHeader={true} {...styles.page}>
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip to main content")}
                destination={afterHeader}
            />
            <EagerLoadingLogoProvider value={true}>
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.brand}>
                        <Zone contentId={id} zoneName="Brand" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.menuContainer}>
                        <Zone contentId={id} zoneName="MainMenu" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainSearch}>
                        <Zone contentId={id} zoneName="MainSearchBox" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttons}>
                        <Button variant="secondary" onClick={() => history.push("/ContactUs")}>
                            <ButtonIcon src="Mail" size={16} />
                            <Typography>Contact Us</Typography>
                        </Button>
                        <Zone contentId={id} zoneName="Account" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.options}>
                        {/* Magnifying glass button for drop-down search box for small screens */}
                        <Clickable 
                            onClick={toggleSearchHandler} 
                            className={`search-button ${isSearchVisible ? 'active' : ''}`}
                        >
                            <Icon src="Search" />
                        </Clickable>
                        <Zone contentId={id} zoneName="Cart" />
                    </StyledWrapper>
                </StyledWrapper>
                {/* Drop-down search box for small screens */}
                <StyledWrapper {...styles.dropDownSearch} css={css`
                    ${styles.dropDownSearch?.css || ''}
                    display: ${isSearchVisible ? 'block' : 'none'};
                `}>
                    <Clickable className="close-link" onClick={closeSearchHandler}>
                        <Icon src="X" />
                        <Typography>Close</Typography>
                    </Clickable>
                    <Zone contentId={id} zoneName="DropDownSearchBox" />
                </StyledWrapper>
                <Zone contentId={id} zoneName="Breadcrumb" />
            </EagerLoadingLogoProvider>
            <span ref={afterHeader} tabIndex={-1} />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;